import React from 'react';
import { FadeIn } from './ui/FadeIn';
import { AlertTriangle, TrendingDown, XCircle, Activity, FileSpreadsheet } from 'lucide-react';
export function PainSection() {
  return <section className="w-full px-4 md:px-6" data-id="element-350">
      <div className="max-w-3xl mx-auto space-y-24" data-id="element-351">
        {/* Part 1: Crisis Truth */}
        <FadeIn data-id="element-352">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-white" data-id="element-353">
            Esto es lo que nadie te dice de las crisis
          </h2>
          <div className="space-y-4 text-lg text-brand-text/90" data-id="element-354">
            <p data-id="element-355">
              Las crisis no "seleccionan" a los más pobres. Ni a los menos
              inteligentes.
            </p>
            <p className="text-xl font-serif text-white" data-id="element-356">
              Las crisis seleccionan a los que están{' '}
              <span className="text-brand-red font-bold" data-id="element-357">
                mal preparados por dentro
              </span>
              .
            </p>
            <p data-id="element-358">
              Y "preparado" no significa tener un portafolio elegante. Significa
              tener tu dinero con reglas tan claras… que aunque el mundo se
              mueva, tú no te rompes.
            </p>
          </div>
        </FadeIn>

        {/* Part 2: Mixed Money Bomb */}
        <FadeIn data-id="element-359">
          <div className="bg-brand-card border border-brand-red/20 rounded-xl p-8 md:p-10 relative overflow-hidden" data-id="element-360">
            <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-361">
              <AlertTriangle size={100} className="text-brand-red" data-id="element-362" />
            </div>

            <h3 className="text-2xl font-bold mb-6 text-white relative z-10" data-id="element-363">
              Si hoy tu dinero está mezclado, estás viviendo con una{' '}
              <span className="text-brand-red" data-id="element-364">bomba silenciosa</span>
            </h3>

            <div className="grid md:grid-cols-2 gap-8 relative z-10" data-id="element-365">
              <div data-id="element-366">
                <h4 className="text-brand-muted text-sm uppercase tracking-wider font-bold mb-4" data-id="element-367">
                  Mezclado significa:
                </h4>
                <ul className="space-y-3" data-id="element-368">
                  {['Una sola cuenta para todo', 'Negocio pagando vida personal', 'Vida personal tapando huecos del negocio', 'Ahorro que se usa en urgencias', 'Inversión hecha con prisa'].map((item, i) => <li key={i} className="flex items-start gap-3 text-brand-text" data-id="element-369">
                      <XCircle className="w-5 h-5 text-brand-red shrink-0 mt-0.5" data-id="element-370" />
                      <span data-id="element-371">{item}</span>
                    </li>)}
                </ul>
              </div>

              <div data-id="element-372">
                <h4 className="text-brand-muted text-sm uppercase tracking-wider font-bold mb-4" data-id="element-373">
                  El resultado predecible:
                </h4>
                <ul className="space-y-3" data-id="element-374">
                  {['Ansiedad constante', 'Decisiones impulsivas', 'Arrepentimiento', "Sensación de 'trabajo pero no avanzo'"].map((item, i) => <li key={i} className="flex items-start gap-3 text-brand-text" data-id="element-375">
                      <TrendingDown className="w-5 h-5 text-brand-red shrink-0 mt-0.5" data-id="element-376" />
                      <span data-id="element-377">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </FadeIn>

        {/* Part 3: Why Old Solutions Fail */}
        <FadeIn data-id="element-378">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-white" data-id="element-379">
            La raíz no es que no sepas. <br data-id="element-380" />
            <span className="text-brand-gold" data-id="element-381">
              La raíz es que no tienes arquitectura.
            </span>
          </h2>

          <p className="text-lg mb-8 text-brand-text/90" data-id="element-382">
            La mayoría consume finanzas como si fueran frases sueltas. Pero si
            no sabes <strong data-id="element-383">qué dinero</strong> hace qué, todo consejo se
            vuelve peligroso.
          </p>

          <div className="grid gap-4 md:grid-cols-2" data-id="element-384">
            {[{
            title: 'Ahorra',
            desc: 'Ahorras sin murallas, y lo rompes con la primera urgencia.',
            icon: <Activity className="w-6 h-6 text-brand-gold" data-id="element-385" />
          }, {
            title: 'Invierte',
            desc: 'Inviertes por FOMO con dinero que era de supervivencia.',
            icon: <TrendingDown className="w-6 h-6 text-brand-gold" data-id="element-386" />
          }, {
            title: 'Diversifica',
            desc: 'Diversificas el desorden.',
            icon: <Activity className="w-6 h-6 text-brand-gold" data-id="element-387" />
          }, {
            title: 'Excel eterno',
            desc: 'Lo haces 2 días y lo abandonas porque no cambia tu comportamiento.',
            icon: <FileSpreadsheet className="w-6 h-6 text-brand-gold" data-id="element-388" />
          }].map((item, i) => <div key={i} className="bg-brand-card/50 p-6 rounded-lg border border-brand-bg hover:border-brand-gold/30 transition-colors" data-id="element-389">
                <div className="flex items-center gap-3 mb-3" data-id="element-390">
                  {item.icon}
                  <h4 className="font-bold text-white text-lg" data-id="element-391">
                    "{item.title}"
                  </h4>
                </div>
                <p className="text-brand-muted text-sm leading-relaxed" data-id="element-392">
                  {item.desc}
                </p>
              </div>)}
          </div>

          <p className="mt-8 text-lg text-brand-text/90 italic border-l-2 border-brand-gold/50 pl-4" data-id="element-393">
            Terminas invirtiendo con dinero que era para sobrevivir. O ahorrando
            en el mismo lugar donde pagas urgencias.
          </p>
        </FadeIn>
      </div>
    </section>;
}